import React, { useState, useEffect } from 'react';
import { EdgeProps, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

export const CustomStraightEdge = (props: EdgeProps) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    sourcePosition,
    targetPosition,
    selected,
    style = {},
  } = props;

  const stroke = style.stroke || '#000';
  const strokeWidth = Number(style.strokeWidth) || 1;
  const interactionWidth = 20;

  const highlightColor = stroke;
  const glowColor = stroke;
  const glowWidth = selected ? strokeWidth * 1.5 : strokeWidth;

  const [control, setControl] = useState<{ x: number; y: number }>({
    x: data?.controlX !== undefined ? Number(data.controlX) : (sourceX + targetX) / 2,
    y: data?.controlY !== undefined ? Number(data.controlY) : (sourceY + targetY) / 2,
  });

  // Store previous endpoints
  const prevEndpoints = React.useRef({ sourceX, sourceY, targetX, targetY });

  useEffect(() => {
    const prev = prevEndpoints.current;
    const oldMid = { x: (prev.sourceX + prev.targetX) / 2, y: (prev.sourceY + prev.targetY) / 2 };
    const offset = { x: control.x - oldMid.x, y: control.y - oldMid.y };
    const newMid = { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 };

    const margin = 20;
    const minX = Math.min(sourceX, targetX) + margin;
    const maxX = Math.max(sourceX, targetX) - margin;
    const minY = Math.min(sourceY, targetY) + margin;
    const maxY = Math.max(sourceY, targetY) - margin;

    const isSourceVertical = sourcePosition?.includes('top') || sourcePosition?.includes('bottom');
    const isTargetVertical = targetPosition?.includes('top') || targetPosition?.includes('bottom');

    if (isSourceVertical && isTargetVertical) {
      const newY = Math.max(minY, Math.min(newMid.y + offset.y, maxY));
      setControl({ x: newMid.x, y: newY });
    } else if (!isSourceVertical && !isTargetVertical) {
      const newX = Math.max(minX, Math.min(newMid.x + offset.x, maxX));
      setControl({ x: newX, y: newMid.y });
    } else {
      const newX = Math.max(minX, Math.min(newMid.x + offset.x, maxX));
      const newY = Math.max(minY, Math.min(newMid.y + offset.y, maxY));
      setControl({ x: newX, y: newY });
    }

    prevEndpoints.current = { sourceX, sourceY, targetX, targetY };
  }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition]);

  // Store control point location in data when control changes
  const { updateEdgeData } = useReactFlow();
  useEffect(() => {
    updateEdgeData(id, { controlX: control.x, controlY: control.y });
  }, [control]);

  const getStraightPath = ({
    sourceX,
    sourceY,
    targetX,
    targetY,
  }: {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
  }) => {
    return `M${sourceX},${sourceY} L${targetX},${targetY}`;
  };

  const edgePath = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  return (
    <>
      <defs>
        {/* No marker here, no arrow */}
        <marker
        id={`arrowhead-${id}`}
        markerWidth="5"
        markerHeight="3.5"
        refX="3"
        refY="1.75"
        orient="auto"
        markerUnits="strokeWidth"
        >
        <polygon points="0 0, 5 1.75, 0 3.5" fill={stroke} />
        </marker>
      </defs>
      {interactionWidth && (
        <path
          d={edgePath}
          fill="none"
          strokeOpacity={0}
          strokeWidth={interactionWidth}
          className="react-flow__edge-interaction"
        />
      )}
      {/* normal path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={style.strokeDasharray}
        filter={selected ? `url(#glow-${id})` : undefined}
        markerEnd={`url(#arrowhead-${id})`}
      />
    </>
  );
};
